import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, collection, getDocs, writeBatch } from 'firebase/firestore';
import { db, auth, storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Settings, CheckSquare, List, Plus, Trash2, RefreshCw, Save, Palette, Image as ImageIcon, LayoutDashboard, Calendar, Edit2, Check, X, ChevronDown, ChevronRight, Download, Upload, MapPin, Search } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import { MODULES } from './Layout';

// Headings are looked up when they are drawn: a module-level constant is built before there is
// a language to look anything up in.
const PARAMETER_CATEGORIES = [
  { key: 'hr', items: [{ id: 'edbRanks' }, { id: 'jobRoles' }] },
  { key: 'campus', items: [{ id: 'locations' }, { id: 'floors' }, { id: 'campusStatus' }] },
  { key: 'inventory', items: [{ id: 'sourceOfFunding' }, { id: 'inventoryCategory' }] },
  { key: 'adminGroup', items: [{ id: 'inventoryCommittee' }, { id: 'departments' }] },
  { key: 'academic', items: [{ id: 'classLevels' }, { id: 'classes' }, { id: 'subjects' }, { id: 'assessments' }] },
];

const DEFAULT_SETTINGS = {
  moduleStatus: MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: true }), {}),
  dropdowns: {
    edbRanks: [
      { code: 'GM', name: 'GM', description: 'Graduate Master' },
      { code: 'PGM', name: 'PGM', description: 'Principal Graduate Master' },
      { code: 'SGM', name: 'SGM', description: 'Senior Graduate Master' },
      { code: 'CM', name: 'CM', description: 'Certificated Master' },
      { code: 'APSM', name: 'APSM', description: 'Assistant Primary School Master' },
      { code: 'PSM', name: 'PSM', description: 'Primary School Master' }
    ],
    jobRoles: [
      { code: 'TCH', name: 'Teaching Staff', description: 'Teachers and principals' },
      { code: 'ADM', name: 'Administrative Staff', description: 'Office and admin personnel' },
      { code: 'SUP', name: 'Supporting Staff', description: 'IT, lab tech, and janitorial' }
    ],
    campusStatus: [
      { code: 'ACT', name: 'Active', description: 'In use' },
      { code: 'MNT', name: 'Under Maintenance', description: 'Currently being repaired' },
      { code: 'CLS', name: 'Closed', description: 'Temporarily unavailable' }
    ],
    sourceOfFunding: [
      { code: 'GEN', name: 'General Funds', description: 'Standard school budget' },
      { code: 'PTA', name: 'PTA Donation', description: 'Parent-Teacher Association' },
      { code: 'GOV', name: 'Government Grant', description: 'Special government funding' },
      { code: 'PRJ', name: 'Special Project', description: 'One-off special projects' }
    ],
    inventoryCategory: [
      { code: 'IT', name: 'IT Equipment', description: 'Computers, projectors, etc.' },
      { code: 'LAB', name: 'Lab Equipment', description: 'Science lab apparatus' },
      { code: 'FURN', name: 'Furniture', description: 'Desks, chairs, cabinets' },
      { code: 'STAT', name: 'Stationery', description: 'Consumable office supplies' }
    ],
    inventoryCommittee: [
      { code: 'IT_COM', name: 'IT Committee', description: 'Handles technology assets' },
      { code: 'SCI_COM', name: 'Science Committee', description: 'Handles lab equipment' },
      { code: 'SPT_COM', name: 'Sports Committee', description: 'Handles PE equipment' }
    ],
    classLevels: [],
    classes: [],
    subjects: [],
    assessments: [],
    locations: [],
    floors: [],
    departments: []
  }
};

export const SystemSettingsContent = ({ activeSchoolId, isDeveloperMode, isMobile }) => {
  const { t } = useT();
  const listName = (id) => t(`systemSettings.lists.${id}`, {}, id);
  const [activeTab, setActiveTab] = useState(isDeveloperMode ? 'parameters' : 'modules');
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    const initial = {};
    PARAMETER_CATEGORIES.forEach((_, idx) => initial[idx] = true);
    return initial;
  });

  const [collapsedSubCategories, setCollapsedSubCategories] = useState(() => {
    const initial = {};
    PARAMETER_CATEGORIES.forEach(cat => {
      cat.items.forEach(item => {
        initial[item.id] = true;
      });
    });
    return initial;
  });

  const toggleCategory = (idx) => {
    setCollapsedCategories(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleSubCategory = (id) => {
    setCollapsedSubCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItemTexts, setNewItemTexts] = useState({});
  const [editingItem, setEditingItem] = useState(null);

  const [branding, setBranding] = useState({
    logoUrl: '',
    // Where the uploaded file lives in Storage. Saved and read back so that replacing a logo can
    // delete the one it replaced -- without it, every upload with a different extension leaves its
    // predecessor in the bucket, counted against the school's quota for good.
    logoPath: '',
    tagline: 'Welcome to the School Portal',
    locationName: '',
    latitude: '',
    longitude: ''
  });
  const [savingBranding, setSavingBranding] = useState(false);

  const [dashboardConfig, setDashboardConfig] = useState({
    calendarUrl: '',
    // Absent means shown. A school that set an embed up before this switch existed is still using
    // it, and reading a missing field as "off" would take it away the moment this deploys.
    hideEmbeddedCalendar: false
  });
  const [savingDashboard, setSavingDashboard] = useState(false);

  useEffect(() => {
    const docRef = isDeveloperMode 
      ? doc(db, 'metadata', 'system_settings')
      : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
      
    const unsub = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        if (data.branding) {
          setBranding({
            logoUrl: data.branding.logoUrl || '',
            logoPath: data.branding.logoPath || '',
            tagline: data.branding.tagline || 'Welcome to the School Portal',
            locationName: data.branding.locationName || '',
            latitude: data.branding.latitude || '',
            longitude: data.branding.longitude || ''
          });
        }
        if (data.dashboardConfig) {
          setDashboardConfig(data.dashboardConfig);
        }
        
        // Ensure all defaults are present if missing
        const mergedDropdowns = { ...DEFAULT_SETTINGS.dropdowns };
        if (data.dropdowns) {
          Object.keys(data.dropdowns).forEach(k => {
            if (data.dropdowns[k] && data.dropdowns[k].length > 0) {
              // Backward compatibility mapping for raw string elements
              mergedDropdowns[k] = data.dropdowns[k].map(item => {
                if (typeof item === 'string') {
                  return { code: item, nameEn: item, nameZh: '', seq: 0, active: true };
                }
                if (!item.code && (item.nameEn || item.name)) {
                  return { ...item, code: item.nameEn || item.name, nameEn: item.nameEn || item.name, nameZh: item.nameZh || '' };
                }
                
                const safeItem = { 
                  code: item.code, 
                  nameEn: item.nameEn || item.name || '', 
                  nameZh: item.nameZh || '', 
                  seq: item.seq || 0, 
                  active: item.active !== false 
                };
                if (item.subjCode !== undefined) safeItem.subjCode = item.subjCode;
                if (item.samsCode !== undefined) safeItem.samsCode = item.samsCode;
                if (item.capacity !== undefined) safeItem.capacity = item.capacity;
                if (item.status !== undefined) safeItem.status = item.status;
                if (item.moi !== undefined) safeItem.moi = item.moi;
                if (item.department !== undefined) safeItem.department = item.department;
                if (item.level !== undefined) safeItem.level = item.level;
                if (item.sub_group !== undefined) safeItem.sub_group = item.sub_group;
                if (item.writtenTest !== undefined) safeItem.writtenTest = item.writtenTest;
                if (item.bldg !== undefined) safeItem.bldg = item.bldg;
                if (item.floor !== undefined) safeItem.floor = item.floor;
                return safeItem;
              });
            }
          });
        }

        setSettings({
          moduleStatus: { ...DEFAULT_SETTINGS.moduleStatus, ...(data.moduleStatus || {}) },
          dropdowns: mergedDropdowns
        });
      } else {
        // Initialize if doesn't exist
        if (!isDeveloperMode) {
          // Fetch global default first
          getDoc(doc(db, 'metadata', 'system_settings')).then(globalSnap => {
            if (globalSnap.exists()) {
              setDoc(docRef, globalSnap.data());
            } else {
              setDoc(docRef, DEFAULT_SETTINGS);
            }
          }).catch(err => console.error("Error fetching global default:", err));
        } else {
          setDoc(docRef, DEFAULT_SETTINGS).catch(err => console.error(err));
        }
      }
      setLoading(false);
    }, (error) => {
      console.error("Error fetching system settings:", error);
      setLoading(false);
    });
    return () => unsub();
  }, [activeSchoolId, isDeveloperMode]);

  const [searchingLocation, setSearchingLocation] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');

  const handleSearchLocation = async () => {
    if (!locationSearchQuery.trim()) return;
    setSearchingLocation(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationSearchQuery)}`);
      const data = await res.json();
      if (data && data.length > 0) {
        const firstResult = data[0];
        setBranding(prev => ({
          ...prev,
          latitude: firstResult.lat,
          longitude: firstResult.lon,
          locationName: prev.locationName || firstResult.display_name.split(',')[0]
        }));
      } else {
        alert(t('systemSettings.locationNotFound'));
      }
    } catch (e) {
      console.error(e);
      alert(t('systemSettings.locationSearchError'));
    }
    setSearchingLocation(false);
  };

  // Uploading the school logo.
  //
  // The file goes to branding/schools/{schoolId}/, not the tenant upload tree, because the login page
  // has to paint it before anybody has signed in -- see storage.rules for why that cannot live under
  // upload/. The download URL is written into the same branding.logoUrl field a pasted link used, so
  // nothing downstream changes: the sidebar, the login page and the PWA manifest all keep reading one
  // field.
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');

  const handleLogoUpload = async (file) => {
    if (!file || !activeSchoolId) return;
    setLogoError('');

    // Checked here as well as in the rules so the person gets a sentence instead of a
    // storage/unauthorized code. The rules are what actually enforce it.
    if (!file.type.startsWith('image/')) {
      setLogoError(t('systemSettings.notAnImage'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError(t('systemSettings.logoTooBig', { size: (file.size / 1024 / 1024).toFixed(1) }));
      return;
    }

    setLogoUploading(true);
    try {
      // A fixed name per school rather than the original filename: re-uploading replaces the logo
      // instead of leaving every previous attempt in the bucket, counting against the school's quota
      // forever. The extension is kept so the content type survives.
      const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '');
      const path = `branding/schools/${activeSchoolId}/logo.${ext}`;
      const fileRef = storageRef(storage, path);
      // uploadBytesResumable, not uploadBytes, and the difference is not style.
      //
      // The rule checks request.resource.size. A one-shot uploadBytes does not declare the length up
      // front, so that property is undefined when the rule runs -- and an undefined property is an
      // evaluation error, which denies. The upload then fails with storage/unauthorized, which reads
      // exactly like a permission problem and is why this took four rounds to find. A resumable upload
      // opens with the content length declared, so the property exists.
      //
      // Every other upload in this app already uses the resumable form. This was the only exception,
      // and the only one that broke.
      await uploadBytesResumable(fileRef, file, { contentType: file.type });
      const url = await getDownloadURL(fileRef);

      // An upload with a different extension leaves the old file behind, so it is removed once the new
      // one is safely up -- in that order, so a failed upload never loses the existing logo.
      const previous = branding.logoPath;
      if (previous && previous !== path) {
        await deleteObject(storageRef(storage, previous)).catch(() => {});
      }

      setBranding(prev => ({ ...prev, logoUrl: url, logoPath: path }));
    } catch (e) {
      console.error('Logo upload failed', e);
      // storage/unauthorized is what the rules return for EVERY refusal -- not an administrator, not
      // a developer, school suspended, school out of storage. Naming one of them would be a guess
      // presented as a fact, so the message says what is actually known and points at the check that
      // can tell them apart.
      setLogoError(
        e?.code === 'storage/unauthorized'
          ? t('systemSettings.uploadRefused')
          : t('systemSettings.uploadFailed', { message: e.message }),
      );
    }
    setLogoUploading(false);
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    if (!activeSchoolId && !isDeveloperMode) return;
    setSavingBranding(true);
    try {
      const docRef = isDeveloperMode 
        ? doc(db, 'metadata', 'system_settings')
        : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
      await setDoc(docRef, { branding }, { merge: true });
      
      if (!isDeveloperMode && activeSchoolId) {
        try {
          await setDoc(doc(db, 'schools', activeSchoolId), {
            sch_basic: {
              logoUrl: branding.logoUrl || '',
              tagline: branding.tagline || '',
              locationName: branding.locationName || ''
            }
          }, { merge: true });
        } catch (e) {
          console.error("Failed to sync branding to school document:", e);
        }
      }

      alert(t('systemSettings.brandingSaved'));
    } catch (err) {
      alert(t('systemSettings.brandingFailed', { message: err.message }));
    }
    setSavingBranding(false);
  };

  const handleSaveDashboardConfig = async (e) => {
    e.preventDefault();
    if (!activeSchoolId && !isDeveloperMode) return;
    setSavingDashboard(true);
    try {
      const docRef = isDeveloperMode 
        ? doc(db, 'metadata', 'system_settings')
        : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
      await setDoc(docRef, { dashboardConfig }, { merge: true });
      alert(t('systemSettings.dashboardSaved'));
    } catch (err) {
      alert(t('systemSettings.dashboardFailed', { message: err.message }));
    }
    setSavingDashboard(false);
  };

  const handleToggleModule = async (moduleId) => {
    const newStatus = !settings.moduleStatus[moduleId];
    const newSettings = {
      ...settings,
      moduleStatus: {
        ...settings.moduleStatus,
        [moduleId]: newStatus
      }
    };
    setSettings(newSettings);
    const docRef = isDeveloperMode 
      ? doc(db, 'metadata', 'system_settings')
      : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
    await setDoc(docRef, newSettings, { merge: true });
  };

  const handleExportCSV = (listId, listName) => {
    const data = settings.dropdowns[listId] || [];
    const isAcademic = listId === 'subjects' || listId === 'assessments';
    
    const csvData = data.map(item => {
      const row = {
        'Code': item.code || '',
        'Name (EN)': item.nameEn || '',
        'Name (ZH)': item.nameZh || '',
        'Seq': item.seq || 0,
        'Active': item.active === false ? 'FALSE' : 'TRUE'
      };
      if (isAcademic) {
        row['SAMS Code'] = item.samsCode || '';
        if (listId === 'subjects') {
          row['MOI'] = item.moi || '';
          row['Department'] = item.department || '';
        }
        if (listId === 'assessments') {
          row['Written Test'] = item.writtenTest === false ? 'FALSE' : 'TRUE';
        }
      }
      if (listId === 'locations') {
        row['BLDG'] = item.bldg || '';
        row['Floor'] = item.floor || '';
        row['Capacity'] = item.capacity || '';
        row['Status'] = item.status || 'ACT';
      }
      if (listId === 'classes') {
        row['Level'] = item.level || '';
        row['Sub-group'] = item.sub_group ? 'Yes' : 'No';
      }
      return row;
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel Chinese support
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${listName.replace(/\s+/g, '_')}_Export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e, listId) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target.result;
      const arr = new Uint8Array(buffer);
      let encoding = 'utf-8';
      try {
        new TextDecoder('utf-8', { fatal: true }).decode(arr);
      } catch (err) {
        encoding = 'Big5';
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: encoding,
        complete: async (results) => {
        const parsedData = results.data;
        const currentList = settings.dropdowns[listId] || [];
        const isAcademic = listId === 'subjects' || listId === 'assessments';

        const mergedList = [...currentList];
        
        parsedData.forEach(row => {
          const code = row['Code']?.trim();
          if (!code) return; // Ignore rows without a code

          const newItem = { code };
          
          if (row['Name (EN)'] !== undefined) newItem.nameEn = row['Name (EN)'].trim();
          if (row['Name (ZH)'] !== undefined) newItem.nameZh = row['Name (ZH)'].trim();
          if (row['Seq'] !== undefined) newItem.seq = parseInt(row['Seq'], 10) || 0;
          if (row['Active'] !== undefined) newItem.active = row['Active'].toUpperCase() !== 'FALSE';

          if (isAcademic) {
            if (row['SAMS Code'] !== undefined) newItem.samsCode = row['SAMS Code'].trim();
            if (listId === 'subjects') {
              if (row['MOI'] !== undefined) newItem.moi = row['MOI'].trim();
              if (row['Department'] !== undefined) newItem.department = String(row['Department']).trim();
              newItem.subjCode = code;
            }
            if (listId === 'assessments' && row['Written Test'] !== undefined) {
              newItem.writtenTest = row['Written Test'].toUpperCase() !== 'FALSE';
            }
          }
          if (listId === 'locations') {
            if (row['BLDG'] !== undefined) newItem.bldg = String(row['BLDG']).trim();
            if (row['Floor'] !== undefined) newItem.floor = String(row['Floor']).trim();
            if (row['Capacity'] !== undefined) {
              const parsedCap = parseInt(String(row['Capacity']).trim(), 10);
              newItem.capacity = isNaN(parsedCap) ? '' : parsedCap;
            }
            if (row['Status'] !== undefined) newItem.status = String(row['Status']).trim() || 'ACT';
          }
          if (listId === 'classes') {
            if (row['Level'] !== undefined) newItem.level = String(row['Level']).trim();
            if (row['Sub-group'] !== undefined) {
              const val = String(row['Sub-group']).toLowerCase().trim();
              newItem.sub_group = val === 'yes' || val === 'true' || val === '1';
            }
          }

          const existingIndex = mergedList.findIndex(item => item.code === code);
          if (existingIndex >= 0) {
            mergedList[existingIndex] = { ...mergedList[existingIndex], ...newItem };
          } else {
            mergedList.push(newItem);
          }
        });

        // Sort by seq automatically
        mergedList.sort((a, b) => (a.seq || 0) - (b.seq || 0));

        const newSettings = {
          ...settings,
          dropdowns: { ...settings.dropdowns, [listId]: mergedList }
        };

        setSettings(newSettings);
        const docRef = isDeveloperMode 
          ? doc(db, 'metadata', 'system_settings')
          : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
        try {
          await setDoc(docRef, newSettings, { merge: true });
          alert(t('systemSettings.importSuccess', { list: listName(listId) }));
        } catch (error) {
          console.error("Error updating document: ", error);
          alert(t('systemSettings.importFailed'));
        }
      },
      error: (err) => {
        alert(t('systemSettings.parseFailed', { message: err.message }));
      }
    });
    };
    reader.readAsArrayBuffer(file);
    e.target.value = null; // reset input
  };

  const handleAddDropdownItem = async (listId) => {
    const newItem = newItemTexts[listId];
    if (!newItem || !newItem.code || !newItem.code.trim() || !newItem.nameEn || !newItem.nameEn.trim()) return;
    
    const currentList = settings.dropdowns[listId] || DEFAULT_SETTINGS.dropdowns[listId] || [];
    if (currentList.some(i => i.code === newItem.code.trim())) {
      alert(t('systemSettings.codeExists'));
      return;
    }

    const itemToSave = {
      code: newItem.code.trim(),
      nameEn: newItem.nameEn.trim(),
      nameZh: (newItem.nameZh || '').trim(),
      seq: parseInt(newItem.seq) || 0,
      active: newItem.active !== false
    };
    if (listId === 'subjects' || listId === 'assessments') {
      if (listId === 'subjects') {
        itemToSave.subjCode = itemToSave.code;
        itemToSave.moi = (newItem.moi || '').trim();
        // Which panel owns this subject. Several subjects can sit under one department -- Chinese
        // Language and Chinese Literature share a panel head -- so this is many-to-one, and it is
        // the link that lets Exam Scope work out who signs a revision scope off. Without it the
        // approver would have to be a hand-kept list, which is the shape this codebase has had to
        // unpick four times.
        itemToSave.department = (newItem.department || '').trim();
      }
      if (listId === 'assessments') {
        itemToSave.writtenTest = newItem.writtenTest !== false;
      }
      itemToSave.samsCode = (newItem.samsCode || '').trim();
    }
    if (listId === 'locations') {
      itemToSave.bldg = (newItem.bldg || '').trim();
      itemToSave.floor = (newItem.floor || '').trim();
      const parsedCap = parseInt(newItem.capacity, 10);
      itemToSave.capacity = isNaN(parsedCap) ? '' : parsedCap;
      itemToSave.status = (newItem.status || 'ACT').trim();
    }
    if (listId === 'classes') {
      itemToSave.level = (newItem.level || '').trim();
      itemToSave.sub_group = !!newItem.sub_group;
    }

    const newList = [...currentList, itemToSave];
    const newSettings = {
      ...settings,
      dropdowns: { ...settings.dropdowns, [listId]: newList }
    };
    setSettings(newSettings);
    setNewItemTexts({ ...newItemTexts, [listId]: { code: '', nameEn: '', nameZh: '', subjCode: '', samsCode: '', seq: '', active: true, capacity: '', status: 'ACT', moi: '', level: '', sub_group: false, writtenTest: true } });
    const docRef = isDeveloperMode 
      ? doc(db, 'metadata', 'system_settings')
      : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
    try { await setDoc(docRef, newSettings, { merge: true }); } catch (e) { alert(t('systemSettings.saveError', { message: e.message })); console.error(e); }
  };

  const handleDeleteDropdownItem = async (listId, itemCode) => {
    const currentList = settings.dropdowns[listId] || [];
    const newList = currentList.filter(i => i.code !== itemCode);
    const newSettings = {
      ...settings,
      dropdowns: { ...settings.dropdowns, [listId]: newList }
    };
    setSettings(newSettings);
    const docRef = isDeveloperMode 
      ? doc(db, 'metadata', 'system_settings')
      : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
    await setDoc(docRef, newSettings, { merge: true });
  };

  const handleSaveEditDropdownItem = async (listId) => {
    try {
      if (!editingItem) return;
      const code = (editingItem.draft.code || '').trim();
      const nameEn = (editingItem.draft.nameEn || '').trim();
      const nameZh = (editingItem.draft.nameZh || '').trim();
      
      if (!code || !nameEn) {
        alert(t('systemSettings.codeAndNameRequired'));
        return;
      }

      const currentList = settings.dropdowns[listId] || [];
      
      if (code !== editingItem.originalCode && currentList.some(i => (i.code || '').trim() === code)) {
        alert(t('systemSettings.codeExists'));
        return;
      }

      const itemToSave = {
        code,
        nameEn,
        nameZh,
        seq: parseInt(editingItem.draft.seq) || 0,
        active: editingItem.draft.active !== false
      };
      if (listId === 'subjects' || listId === 'assessments') {
        if (listId === 'subjects') {
          itemToSave.subjCode = itemToSave.code;
          itemToSave.moi = (editingItem.draft.moi || '').trim();
          itemToSave.department = (editingItem.draft.department || '').trim();
        }
        if (listId === 'assessments') {
          itemToSave.writtenTest = editingItem.draft.writtenTest !== false;
        }
        itemToSave.samsCode = (editingItem.draft.samsCode || '').trim();
      }
      if (listId === 'locations') {
        itemToSave.bldg = (editingItem.draft.bldg || '').trim();
        itemToSave.floor = (editingItem.draft.floor || '').trim();
        const parsedCap = parseInt(editingItem.draft.capacity, 10);
        itemToSave.capacity = isNaN(parsedCap) ? '' : parsedCap;
        itemToSave.status = (editingItem.draft.status || 'ACT').trim();
      }
      if (listId === 'classes') {
        itemToSave.level = (editingItem.draft.level || '').trim();
        itemToSave.sub_group = !!editingItem.draft.sub_group;
      }

      const newList = currentList.map(i => i.code === editingItem.originalCode ? itemToSave : i);
      
      const newSettings = {
        ...settings,
        dropdowns: { ...settings.dropdowns, [listId]: newList }
      };
      
      setSettings(newSettings);
      setEditingItem(null);
      const docRef = isDeveloperMode 
        ? doc(db, 'metadata', 'system_settings')
        : doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
      await setDoc(docRef, newSettings, { merge: true });
    } catch (err) {
      console.error("Save Edit Error:", err);
      alert(t('systemSettings.savingError', { message: err.message }));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <RefreshCw className="animate-spin" size={24} color="var(--color-text-muted)" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            !isDeveloperMode && { id: 'dashboard', label: t('systemSettings.tabs.dashboard'), icon: LayoutDashboard },
            { id: 'parameters', label: t('systemSettings.tabs.parameters'), icon: List },
            { id: 'modules', label: t('systemSettings.tabs.modules'), icon: CheckSquare }
          ].filter(Boolean).map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', padding: isMobile ? '0.75rem' : '1.5rem' }}>

        {activeTab === 'modules' && (
          <div style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1.5rem' }}>{t('systemSettings.toggleModules')}</h3>
            {Object.entries(
              MODULES.reduce((acc, module) => {
                if (!acc[module.category]) acc[module.category] = [];
                acc[module.category].push({
                  ...module,
                  locked: module.id === 'user_mgmt' || module.id === 'system_settings'
                });
                return acc;
              }, {})
            ).map(([category, categoryModules]) => {
              const categoryLabel = (c) => t(`systemSettings.moduleCategories.${c}`, {}, c);
              return (
                <div key={category} style={{ marginBottom: '2rem' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
                    {categoryLabel(category)}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(250px, 100%), 1fr))', gap: '1rem' }}>
                    {categoryModules.map(module => (
                      <div key={module.id} style={{ 
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '1rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                        backgroundColor: settings.moduleStatus[module.id] ? 'var(--color-bg-app)' : 'var(--color-bg-subtle)',
                        opacity: module.locked ? 0.6 : 1
                      }}>
                        <div>
                          <strong style={{ fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{t(`nav.modules.${module.id}`, {}, module.name)}</strong>
                          {module.locked && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('systemSettings.required')}</div>}
                        </div>
                        
                        <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', opacity: module.locked ? 0.5 : 1 }}>
                          <input 
                            type="checkbox" 
                            style={{ opacity: 0, width: 0, height: 0 }}
                            checked={settings.moduleStatus[module.id]}
                            onChange={() => !module.locked && handleToggleModule(module.id)}
                            disabled={module.locked}
                          />
                          <span style={{
                            position: 'absolute', cursor: module.locked ? 'not-allowed' : 'pointer',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: settings.moduleStatus[module.id] ? 'var(--color-primary)' : '#ccc',
                            transition: '0.4s', borderRadius: '34px'
                          }}>
                            <span style={{
                              position: 'absolute', content: '""',
                              height: '18px', width: '18px', left: '3px', bottom: '3px',
                              backgroundColor: 'white', transition: '0.4s', borderRadius: '50%',
                              transform: settings.moduleStatus[module.id] ? 'translateX(20px)' : 'translateX(0)'
                            }}></span>
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'parameters' && (
          <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '-1rem' }}>{t('systemSettings.tabs.parameters')}</h3>
            
            {PARAMETER_CATEGORIES.map((categoryGroup, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div 
                  onClick={() => toggleCategory(idx)}
                  style={{ 
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                    cursor: 'pointer', borderBottom: '2px solid var(--color-border)', paddingBottom: '0.5rem' 
                  }}
                >
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--color-primary)', margin: 0 }}>
                    {t(`systemSettings.categories.${categoryGroup.key}`)}
                  </h4>
                  <div style={{ color: 'var(--color-text-muted)' }}>
                    {collapsedCategories[idx] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>
                
                {!collapsedCategories[idx] && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {categoryGroup.items.map(listConfig => {
                    const currentList = settings.dropdowns[listConfig.id] || [];
                    const currentDraft = newItemTexts[listConfig.id] || { nameEn: '', nameZh: '', subjCode: '', samsCode: '' };

                    return (
                      <div key={listConfig.id} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                        
                        {/* List Header & Add Form */}
                        <div style={{ padding: '1rem 1.5rem', backgroundColor: 'var(--color-bg-app)', borderBottom: collapsedSubCategories[listConfig.id] ? 'none' : '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsedSubCategories[listConfig.id] ? '0' : '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSubCategory(listConfig.id)}>
                              {collapsedSubCategories[listConfig.id] ? <ChevronRight size={18} color="var(--color-text-muted)" /> : <ChevronDown size={18} color="var(--color-text-muted)" />}
                              <strong style={{ fontSize: '1rem', color: 'var(--color-text-main)' }}>{listName(listConfig.id)}</strong>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => handleExportCSV(listConfig.id, listName(listConfig.id))} title={t('systemSettings.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                                <Download size={14} />
                              </button>
                              <label title={t('systemSettings.importCsvMerge')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                                <Upload size={14} />
                                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => handleImportCSV(e, listConfig.id)} />
                              </label>
                            </div>
                          </div>
                          
                          {!collapsedSubCategories[listConfig.id] && (
                          <form 
                            onSubmit={(e) => { e.preventDefault(); handleAddDropdownItem(listConfig.id); }} 
                            style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}
                          >
                            <div style={{ flex: '0 0 80px' }}>
                              <input 
                                type="number" 
                                placeholder={t('systemSettings.seq')} 
                                value={currentDraft.seq || ''} 
                                onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, seq: e.target.value}})}
                                style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                              />
                            </div>
                            <div style={{ flex: '1 1 120px' }}>
                              <input 
                                type="text" 
                                placeholder={t('systemSettings.codeRequired')} 
                                required
                                value={currentDraft.code || ''} 
                                onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, code: e.target.value.toUpperCase()}})}
                                style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', fontFamily: 'monospace' }}
                              />
                            </div>
                            <div style={{ flex: '2 1 180px' }}>
                              <input 
                                type="text" 
                                placeholder={t('systemSettings.nameEnRequired')} 
                                required
                                value={currentDraft.nameEn || ''} 
                                onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, nameEn: e.target.value}})}
                                style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                              />
                            </div>
                            <div style={{ flex: '2 1 180px' }}>
                              <input 
                                type="text" 
                                placeholder={t('systemSettings.nameZh')} 
                                value={currentDraft.nameZh || ''} 
                                onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, nameZh: e.target.value}})}
                                style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                              />
                            </div>
                            {(listConfig.id === 'subjects' || listConfig.id === 'assessments') && (
                              <>
                                <div style={{ flex: '1 1 120px' }}>
                                  <input 
                                    type="text" 
                                    placeholder={t('systemSettings.samsCode')} 
                                    value={currentDraft.samsCode || ''} 
                                    onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, samsCode: e.target.value}})}
                                    style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                  />
                                </div>
                                {listConfig.id === 'subjects' && (
                                  <div style={{ flex: '1 1 140px' }}>
                                    <select
                                      value={currentDraft.department || ''}
                                      onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, department: e.target.value}})}
                                      style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                    >
                                      <option value="">{t('systemSettings.departmentNone')}</option>
                                      {(settings?.dropdowns?.departments || []).filter(d => d.active !== false).map(dep => (
                                        <option key={dep.code} value={dep.code}>{dep.name || dep.code}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {listConfig.id === 'subjects' && (
                                  <div style={{ flex: '1 1 120px' }}>
                                    <input
                                      type="text"
                                      placeholder={t('systemSettings.moi')}
                                      value={currentDraft.moi || ''}
                                      onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, moi: e.target.value}})}
                                      style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                    />
                                  </div>
                                )}
                                {listConfig.id === 'assessments' && (
                                  <div style={{ flex: '0 0 130px', display: 'flex', alignItems: 'center', height: '36px' }}>
                                    <label style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                                      <input
                                        type="checkbox"
                                        checked={currentDraft.writtenTest !== false}
                                        onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, writtenTest: e.target.checked}})}
                                        style={{ cursor: 'pointer' }}
                                      /> Written Test
                                    </label>
                                  </div>
                                )}
                              </>
                            )}
                            {listConfig.id === 'locations' && (
                              <>
                                <div style={{ flex: '1 1 100px' }}>
                                  <input 
                                    type="text" 
                                    placeholder={t('systemSettings.bldg')} 
                                    value={currentDraft.bldg || ''} 
                                    onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, bldg: e.target.value}})}
                                    style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                  />
                                </div>
                                <div style={{ flex: '1 1 120px' }}>
                                  <select 
                                    value={currentDraft.floor || ''} 
                                    onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, floor: e.target.value}})}
                                    style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                  >
                                    <option value="">{t('systemSettings.selectFloor')}</option>
                                    {(settings?.dropdowns?.floors || []).filter(f => f.active !== false).map(flr => (
                                      <option key={flr.code} value={flr.code}>{flr.nameEn || flr.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: '1 1 100px' }}>
                                  <input 
                                    type="number" 
                                    placeholder={t('systemSettings.capacity')} 
                                    value={currentDraft.capacity || ''} 
                                    onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, capacity: e.target.value}})}
                                    style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                  />
                                </div>
                                <div style={{ flex: '1 1 120px' }}>
                                  <select 
                                    value={currentDraft.status || 'ACT'} 
                                    onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, status: e.target.value}})}
                                    style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                  >
                                    {(settings?.dropdowns?.campusStatus || []).filter(s => s.active !== false).map(status => (
                                      <option key={status.code} value={status.code}>{status.nameEn || status.name}</option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            )}
                            {listConfig.id === 'classes' && (
                              <>
                                <div style={{ flex: '1 1 120px' }}>
                                  <select 
                                    value={currentDraft.level || ''} 
                                    onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, level: e.target.value}})}
                                    style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                    required
                                  >
                                    <option value="">{t('systemSettings.selectLevel')}</option>
                                    {(settings?.dropdowns?.classLevels || []).filter(l => l.active !== false).map(lvl => (
                                      <option key={lvl.code} value={lvl.code}>{lvl.nameEn || lvl.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: '0 0 100px', display: 'flex', alignItems: 'center', height: '36px' }}>
                                  <label style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                                    <input 
                                      type="checkbox" 
                                      checked={!!currentDraft.sub_group} 
                                      onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, sub_group: e.target.checked}})}
                                      style={{ cursor: 'pointer' }}
                                    /> Sub-group
                                  </label>
                                </div>
                              </>
                            )}
                            <div style={{ flex: '0 0 80px', display: 'flex', alignItems: 'center', height: '36px' }}>
                              <label style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                                <input 
                                  type="checkbox" 
                                  checked={currentDraft.active !== false} 
                                  onChange={e => setNewItemTexts({...newItemTexts, [listConfig.id]: {...currentDraft, active: e.target.checked}})}
                                  style={{ cursor: 'pointer' }}
                                /> Active
                              </label>
                            </div>
                            <button type="submit" className="btn-primary" style={{ width: 'auto', whiteSpace: 'nowrap', padding: '0.5rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem', height: '36px', flex: '0 0 auto' }}>
                              <Plus size={16} /> Add
                            </button>
                          </form>
                          )}
                        </div>

                        {/* List Items Table */}
                        {!collapsedSubCategories[listConfig.id] && (
                        <div>
                          {currentList.length === 0 ? (
                            <div style={{ padding: '1rem 1.5rem', color: 'var(--color-text-muted)', fontSize: '0.875rem', fontStyle: 'italic' }}>{t('systemSettings.noParameters')}</div>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                  <tr style={{ backgroundColor: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)', fontSize: '0.75rem', color: 'var(--color-text-subtle)', textTransform: 'uppercase' }}>
                                    <th style={{ padding: '0.75rem 1.5rem', width: '60px' }}>{t('systemSettings.seq')}</th>
                                    <th style={{ padding: '0.75rem 1.5rem', width: '100px' }}>{t('systemSettings.code')}</th>
                                    <th style={{ padding: '0.75rem 1.5rem' }}>{t('systemSettings.nameEn')}</th>
                                    <th style={{ padding: '0.75rem 1.5rem' }}>{t('systemSettings.nameZh')}</th>
                                    {(listConfig.id === 'subjects' || listConfig.id === 'assessments') && (
                                      <>
                                        <th style={{ padding: '0.75rem 1.5rem' }}>{t('systemSettings.samsCodeCol')}</th>
                                        {listConfig.id === 'subjects' && <th style={{ padding: '0.75rem 1.5rem' }}>{t('systemSettings.department')}</th>}
                                        {listConfig.id === 'subjects' && <th style={{ padding: '0.75rem 1.5rem' }}>{t('systemSettings.moi')}</th>}
                                        {listConfig.id === 'assessments' && <th style={{ padding: '0.75rem 1.5rem', width: '110px' }}>{t('systemSettings.writtenTest')}</th>}
                                      </>
                                    )}
                                    {listConfig.id === 'locations' && (
                                      <>
                                        <th style={{ padding: '0.75rem 1.5rem', width: '80px' }}>{t('systemSettings.bldg')}</th>
                                        <th style={{ padding: '0.75rem 1.5rem', width: '80px' }}>{t('systemSettings.floor')}</th>
                                        <th style={{ padding: '0.75rem 1.5rem', width: '80px' }}>{t('systemSettings.capacity')}</th>
                                        <th style={{ padding: '0.75rem 1.5rem', width: '120px' }}>{t('systemSettings.status')}</th>
                                      </>
                                    )}
                                    {listConfig.id === 'classes' && (
                                      <>
                                        <th style={{ padding: '0.75rem 1.5rem', width: '120px' }}>{t('systemSettings.level')}</th>
                                        <th style={{ padding: '0.75rem 1.5rem', width: '100px' }}>{t('systemSettings.subGroup')}</th>
                                      </>
                                    )}
                                    <th style={{ padding: '0.75rem 1.5rem', width: '80px' }}>{t('systemSettings.active')}</th>
                                    <th style={{ padding: '0.75rem 1.5rem', width: '60px' }}>{t('systemSettings.action')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...currentList].sort((a, b) => (a.seq || 0) - (b.seq || 0)).map(item => {
                                    const isEditing = editingItem && editingItem.listId === listConfig.id && editingItem.originalCode === item.code;
                                    const isDefault = (DEFAULT_SETTINGS.dropdowns[listConfig.id] || []).some(def => def.code === item.code);
                                    
                                    if (isEditing) {
                                      return (
                                        <tr key={item.code} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)' }}>
                                          <td style={{ padding: '0.5rem 1.5rem' }}>
                                            <input 
                                              type="number" 
                                              value={editingItem.draft.seq || ''} 
                                              onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, seq: e.target.value}})}
                                              style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                            />
                                          </td>
                                          <td style={{ padding: '0.5rem 1.5rem' }}>
                                            <input 
                                              type="text" 
                                              value={editingItem.draft.code} 
                                              onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, code: e.target.value.toUpperCase()}})}
                                              style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', fontFamily: 'monospace' }}
                                            />
                                          </td>
                                          <td style={{ padding: '0.5rem 1.5rem' }}>
                                            <input 
                                              type="text" 
                                              value={editingItem.draft.nameEn || ''} 
                                              onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, nameEn: e.target.value}})}
                                              style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', fontWeight: '500' }}
                                            />
                                          </td>
                                          <td style={{ padding: '0.5rem 1.5rem' }}>
                                            <input 
                                              type="text" 
                                              value={editingItem.draft.nameZh || ''} 
                                              onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, nameZh: e.target.value}})}
                                              style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', fontWeight: '500' }}
                                            />
                                          </td>
                                          {(listConfig.id === 'subjects' || listConfig.id === 'assessments') && (
                                            <>
                                              <td style={{ padding: '0.5rem 1.5rem' }}>
                                                <input 
                                                  type="text" 
                                                  value={editingItem.draft.samsCode || ''} 
                                                  onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, samsCode: e.target.value}})}
                                                  style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', fontFamily: 'monospace' }}
                                                />
                                              </td>
                                              {listConfig.id === 'subjects' && (
                                                <td style={{ padding: '0.5rem 1.5rem' }}>
                                                  <select
                                                    value={editingItem.draft.department || ''}
                                                    onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, department: e.target.value}})}
                                                    style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                                  >
                                                    <option value="">(none)</option>
                                                    {(settings?.dropdowns?.departments || []).filter(d => d.active !== false).map(dep => (
                                                      <option key={dep.code} value={dep.code}>{dep.name || dep.code}</option>
                                                    ))}
                                                  </select>
                                                </td>
                                              )}
                                              {listConfig.id === 'subjects' && (
                                                <td style={{ padding: '0.5rem 1.5rem' }}>
                                                  <input
                                                    type="text"
                                                    value={editingItem.draft.moi || ''}
                                                    onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, moi: e.target.value}})}
                                                    style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                                  />
                                                </td>
                                              )}
                                              {listConfig.id === 'assessments' && (
                                                <td style={{ padding: '0.5rem 1.5rem', textAlign: 'center' }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={editingItem.draft.writtenTest !== false}
                                                    onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, writtenTest: e.target.checked}})}
                                                    style={{ cursor: 'pointer' }}
                                                  />
                                                </td>
                                              )}
                                            </>
                                          )}
                                          {listConfig.id === 'locations' && (
                                            <>
                                              <td style={{ padding: '0.5rem 1.5rem' }}>
                                                <input 
                                                  type="text" 
                                                  value={editingItem.draft.bldg || ''} 
                                                  onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, bldg: e.target.value}})}
                                                  style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                                />
                                              </td>
                                              <td style={{ padding: '0.5rem 1.5rem' }}>
                                                <select 
                                                  value={editingItem.draft.floor || ''} 
                                                  onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, floor: e.target.value}})}
                                                  style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                                >
                                                  <option value="">{t('systemSettings.selectFloor')}</option>
                                                  {(settings?.dropdowns?.floors || []).filter(f => f.active !== false).map(flr => (
                                                    <option key={flr.code} value={flr.code}>{flr.nameEn || flr.name}</option>
                                                  ))}
                                                </select>
                                              </td>
                                              <td style={{ padding: '0.5rem 1.5rem' }}>
                                                <input 
                                                  type="number" 
                                                  value={editingItem.draft.capacity || ''} 
                                                  onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, capacity: e.target.value}})}
                                                  style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem' }}
                                                />
                                              </td>
                                              <td style={{ padding: '0.5rem 1.5rem' }}>
                                                <select 
                                                  value={editingItem.draft.status || 'ACT'} 
                                                  onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, status: e.target.value}})}
                                                  style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                                >
                                                  {(settings?.dropdowns?.campusStatus || []).filter(s => s.active !== false).map(status => (
                                                    <option key={status.code} value={status.code}>{status.nameEn || status.name}</option>
                                                  ))}
                                                </select>
                                              </td>
                                              </>
                                            )}
                                            {listConfig.id === 'classes' && (
                                              <>
                                                <td style={{ padding: '0.5rem 1.5rem' }}>
                                                  <select 
                                                    value={editingItem.draft.level || ''} 
                                                    onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, level: e.target.value}})}
                                                    style={{ width: '100%', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)' }}
                                                    required
                                                  >
                                                    <option value="">{t('systemSettings.selectLevel')}</option>
                                                    {(settings?.dropdowns?.classLevels || []).filter(l => l.active !== false).map(lvl => (
                                                      <option key={lvl.code} value={lvl.code}>{lvl.nameEn || lvl.name}</option>
                                                    ))}
                                                  </select>
                                                </td>
                                                <td style={{ padding: '0.5rem 1.5rem', textAlign: 'center' }}>
                                                  <input 
                                                    type="checkbox" 
                                                    checked={!!editingItem.draft.sub_group} 
                                                    onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, sub_group: e.target.checked}})}
                                                    style={{ cursor: 'pointer' }}
                                                  />
                                                </td>
                                              </>
                                            )}
                                          <td style={{ padding: '0.5rem 1.5rem', textAlign: 'center' }}>
                                            <input 
                                              type="checkbox" 
                                              checked={editingItem.draft.active !== false} 
                                              onChange={e => setEditingItem({...editingItem, draft: {...editingItem.draft, active: e.target.checked}})}
                                              style={{ cursor: 'pointer' }}
                                            />
                                          </td>
                                          <td style={{ padding: '0.5rem 1.5rem', display: 'flex', gap: '0.25rem' }}>
                                            <button 
                                              onClick={() => handleSaveEditDropdownItem(listConfig.id)} 
                                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)', padding: '0.2rem', borderRadius: '4px' }}
                                              title={t('common.save')}
                                            >
                                              <Check size={16} />
                                            </button>
                                            <button 
                                              onClick={() => setEditingItem(null)} 
                                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0.2rem', borderRadius: '4px' }}
                                              title={t('common.cancel')}
                                            >
                                              <X size={16} />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    }

                                    return (
                                      <tr key={item.code} style={{ borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-main)', opacity: item.active === false ? 0.6 : 1 }}>
                                        <td style={{ padding: '0.75rem 1.5rem', color: 'var(--color-text-muted)' }}>{item.seq || 0}</td>
                                        <td style={{ padding: '0.75rem 1.5rem', fontFamily: 'monospace', fontWeight: '600' }}>{item.code}</td>
                                        <td style={{ padding: '0.75rem 1.5rem', fontWeight: '500' }}>{item.nameEn || item.name}</td>
                                        <td style={{ padding: '0.75rem 1.5rem', fontWeight: '500' }}>{item.nameZh || ''}</td>
                                        {(listConfig.id === 'subjects' || listConfig.id === 'assessments') && (
                                          <>
                                            <td style={{ padding: '0.75rem 1.5rem', fontFamily: 'monospace' }}>{item.samsCode || '-'}</td>
                                            {listConfig.id === 'subjects' && (
                                              <td style={{ padding: '0.75rem 1.5rem' }}>
                                                {(() => {
                                                  // Shown by its name, not its code -- the code is what the
                                                  // record stores, but "MATH" is not what a school calls it.
                                                  const dep = (settings?.dropdowns?.departments || []).find(d => d.code === item.department);
                                                  return dep ? (dep.name || dep.code) : (item.department || '-');
                                                })()}
                                              </td>
                                            )}
                                            {listConfig.id === 'subjects' && <td style={{ padding: '0.75rem 1.5rem' }}>{item.moi || '-'}</td>}
                                            {listConfig.id === 'assessments' && (
                                              <td style={{ padding: '0.75rem 1.5rem' }}>
                                                <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: item.writtenTest !== false ? 'var(--color-success-bg)' : 'var(--color-bg-subtle)', color: item.writtenTest !== false ? 'var(--color-success-text)' : 'var(--color-text-subtle)' }}>
                                                  {item.writtenTest !== false ? 'Yes' : 'No'}
                                                </span>
                                              </td>
                                            )}
                                          </>
                                        )}
                                        {listConfig.id === 'locations' && (
                                          <>
                                            <td style={{ padding: '0.75rem 1.5rem' }}>{item.bldg || '-'}</td>
                                            <td style={{ padding: '0.75rem 1.5rem' }}>
                                              {(() => {
                                                const fCode = item.floor;
                                                const floorObj = (settings?.dropdowns?.floors || []).find(f => f.code === fCode);
                                                return floorObj ? (floorObj.nameEn || floorObj.name) : (fCode || '-');
                                              })()}
                                            </td>
                                            <td style={{ padding: '0.75rem 1.5rem' }}>{item.capacity !== '' && item.capacity !== undefined ? item.capacity : '-'}</td>
                                            <td style={{ padding: '0.75rem 1.5rem' }}>
                                              {(() => {
                                                const sCode = item.status || 'ACT';
                                                const statusObj = (settings?.dropdowns?.campusStatus || []).find(s => s.code === sCode);
                                                return statusObj ? (statusObj.nameEn || statusObj.name) : sCode;
                                              })()}
                                            </td>
                                          </>
                                        )}
                                        {listConfig.id === 'classes' && (
                                          <>
                                            <td style={{ padding: '0.75rem 1.5rem' }}>
                                              {(() => {
                                                const lCode = item.level;
                                                const lvlObj = (settings?.dropdowns?.classLevels || []).find(l => l.code === lCode);
                                                return lvlObj ? (lvlObj.nameEn || lvlObj.name) : (lCode || '-');
                                              })()}
                                            </td>
                                            <td style={{ padding: '0.75rem 1.5rem' }}>
                                              <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: item.sub_group ? 'var(--color-success-bg)' : 'var(--color-bg-subtle)', color: item.sub_group ? 'var(--color-success-text)' : 'var(--color-text-subtle)' }}>
                                                {item.sub_group ? 'Yes' : 'No'}
                                              </span>
                                            </td>
                                          </>
                                        )}
                                        <td style={{ padding: '0.75rem 1.5rem' }}>
                                          <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: item.active !== false ? 'var(--color-success-bg)' : 'var(--color-danger-bg)', color: item.active !== false ? 'var(--color-success-text)' : 'var(--color-danger-text)' }}>
                                            {item.active !== false ? 'Yes' : 'No'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '0.75rem 1.5rem', display: 'flex', gap: '0.25rem' }}>
                                          <button 
                                            onClick={() => setEditingItem({ listId: listConfig.id, originalCode: item.code, draft: { ...item, nameEn: item.nameEn || item.name || '' } })} 
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: '0.2rem', borderRadius: '4px' }}
                                            title={t('systemSettings.editItem')}
                                          >
                                            <Edit2 size={16} />
                                          </button>
                                          {!isDefault && (
                                            <button 
                                              onClick={() => handleDeleteDropdownItem(listConfig.id, item.code)} 
                                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.2rem', borderRadius: '4px' }}
                                              title={t('systemSettings.deleteItem')}
                                            >
                                              <Trash2 size={16} />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            ))}
            

            
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1.5rem' }}>{t('systemSettings.basicTitle')}</h3>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '2rem', marginBottom: '2rem' }}>
              <form onSubmit={handleSaveBranding} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ImageIcon size={16} /> School Logo
                  </label>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    {branding.logoUrl ? (
                      <div style={{ padding: '1rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '104px', minHeight: '104px' }}>
                        <img src={branding.logoUrl} alt="School logo" style={{ maxHeight: '72px', maxWidth: '72px', objectFit: 'contain' }} />
                      </div>
                    ) : (
                      <div style={{ padding: '1rem', backgroundColor: 'var(--color-bg-subtle)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '104px', minHeight: '104px', color: 'var(--color-text-muted)' }}>
                        <ImageIcon size={28} style={{ opacity: 0.4 }} />
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label
                        className="btn-outline"
                        style={{ width: 'auto', padding: '0.5rem 1rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: logoUploading ? 'default' : 'pointer', opacity: logoUploading ? 0.6 : 1 }}
                      >
                        <Upload size={16} />
                        {logoUploading ? t('systemSettings.uploading') : (branding.logoUrl ? t('systemSettings.replaceLogo') : t('systemSettings.uploadLogo'))}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp"
                          disabled={logoUploading}
                          onChange={(e) => { handleLogoUpload(e.target.files?.[0]); e.target.value = ''; }}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {branding.logoUrl && (
                        <button
                          type="button"
                          onClick={() => setBranding(prev => ({ ...prev, logoUrl: '', logoPath: '' }))}
                          style={{ background: 'none', border: 'none', padding: 0, fontSize: '0.8rem', color: 'var(--color-danger)', cursor: 'pointer', textAlign: 'left' }}
                        >
                          {t('systemSettings.remove')}
                        </button>
                      )}
                    </div>
                  </div>

                  {logoError && (
                    <p style={{ fontSize: '0.82rem', color: 'var(--color-danger-text)', backgroundColor: 'var(--color-danger-bg)', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-md)', marginTop: '0.75rem' }}>
                      {logoError}
                    </p>
                  )}

                  {/* Kept, because a school that already hosts its crest somewhere should not have to
                      re-upload it, and because this is the field the uploader fills -- seeing it makes
                      that obvious rather than magic. */}
                  <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '1rem', marginBottom: '0.3rem' }}>
                    …or paste a direct image link
                  </label>
                  <input
                    type="url"
                    className="input-field"
                    placeholder={t('systemSettings.logoUrlPlaceholder')}
                    value={branding.logoUrl}
                    onChange={(e) => setBranding({ ...branding, logoUrl: e.target.value, logoPath: '' })}
                  />

                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                    {t('systemSettings.logoHint')}
                  </p>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)' }} />

                <div>
                  <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {t('systemSettings.tagline')}
                  </label>
                  <input 
                    type="text" 
                    className="input-field" 
                    placeholder={t('systemSettings.taglinePlaceholder')}
                    value={branding.tagline}
                    onChange={(e) => setBranding({...branding, tagline: e.target.value})}
                  />
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                    {t('systemSettings.taglineHint')}
                  </p>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)' }} />

                <div>
                  <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MapPin size={16} /> School Location
                  </label>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder={t('systemSettings.searchAddress')}
                        value={locationSearchQuery}
                        onChange={(e) => setLocationSearchQuery(e.target.value)}
                        style={{ paddingLeft: '2.25rem' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearchLocation();
                          }
                        }}
                      />
                    </div>
                    <button 
                      type="button" 
                      className="btn-outline" 
                      onClick={handleSearchLocation}
                      disabled={searchingLocation}
                      style={{ width: 'auto', whiteSpace: 'nowrap' }}
                    >
                      {searchingLocation ? t('systemSettings.searching') : t('systemSettings.findCoordinates')}
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)', marginBottom: '0.25rem', display: 'block' }}>{t('systemSettings.latitude')}</label>
                      <input type="text" className="input-field" readOnly value={branding.latitude || ''} placeholder="-" style={{ backgroundColor: 'var(--color-bg-app)' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)', marginBottom: '0.25rem', display: 'block' }}>{t('systemSettings.longitude')}</label>
                      <input type="text" className="input-field" readOnly value={branding.longitude || ''} placeholder="-" style={{ backgroundColor: 'var(--color-bg-app)' }} />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)', marginBottom: '0.25rem', display: 'block' }}>{t('systemSettings.displayName')}</label>
                    <input 
                      type="text" 
                      className="input-field" 
                      placeholder={t('systemSettings.displayNamePlaceholder')}
                      value={branding.locationName}
                      onChange={(e) => setBranding({...branding, locationName: e.target.value})}
                    />
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                      {t('systemSettings.weatherHint')}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button type="submit" className="btn-primary" disabled={savingBranding} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Save size={18} /> {savingBranding ? t('common.saving') : t('systemSettings.saveSchoolSettings')}
                  </button>
                </div>
              </form>
            </div>

            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '2rem' }}>
              <form onSubmit={handleSaveDashboardConfig} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                
                <div>
                  <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={16} /> Google Calendar Embed URL
                  </label>
                  <input 
                    type="url" 
                    className="input-field" 
                    placeholder={t('systemSettings.calendarUrlPlaceholder')}
                    value={dashboardConfig.calendarUrl}
                    onChange={(e) => setDashboardConfig({...dashboardConfig, calendarUrl: e.target.value})}
                  />
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                    {t('systemSettings.calendarHint')}
                  </p>
                </div>

                {/* Hiding rather than clearing. A school that wants the card off the dashboard for a
                    term should not have to find the embed URL again to put it back, and pasting a
                    long URL from memory is how it ends up pointing at the wrong calendar. */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: dashboardConfig.calendarUrl ? 'pointer' : 'default', opacity: dashboardConfig.calendarUrl ? 1 : 0.5 }}>
                    <input
                      type="checkbox"
                      checked={!!dashboardConfig.hideEmbeddedCalendar}
                      disabled={!dashboardConfig.calendarUrl}
                      onChange={(e) => setDashboardConfig({ ...dashboardConfig, hideEmbeddedCalendar: e.target.checked })}
                      style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary)', cursor: 'inherit' }}
                    />
                    <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{t('systemSettings.hideEmbed')}</span>
                  </label>
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                    {dashboardConfig.calendarUrl
                      ? t('systemSettings.hideEmbedHint')
                      : t('systemSettings.nothingToHide')}
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button type="submit" className="btn-primary" disabled={savingDashboard} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Save size={18} /> {savingDashboard ? t('common.saving') : t('systemSettings.saveSettings')}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const SystemSettings = () => {
  const { activeSchoolId, isMobile } = useOutletContext();
  return <SystemSettingsContent activeSchoolId={activeSchoolId} isMobile={isMobile} />;
};

export default SystemSettings;
